import dayjs from "dayjs";

import { req } from "./priceDirectus";
import {
  buildProvinceQueryClauses,
  getProvinceOptionList,
  toProv,
} from "../components/Analytics/PriceReports/provinceUtils";

/** Normalize `product_id` on Directus aggregate rows (string UUID or `{ id }`). */
const normalizeAggregateProductIdFromRow = (row) => {
  const raw = row?.product_id;
  if (raw == null) return "";
  if (typeof raw === "object" && raw.id != null) return String(raw.id);
  return String(raw);
};

const coerceRangeForPriceHistoryFilter = (range = {}) => ({
  startDate: range?.startDate ? dayjs(range.startDate).format("YYYY-MM-DD") : undefined,
  endDate: range?.endDate ? dayjs(range.endDate).format("YYYY-MM-DD") : undefined,
});

const bucketMap = {
  day: ["year(record_at)", "month(record_at)", "day(record_at)"],
  month: ["year(record_at)", "month(record_at)"],
};

const getDimField = (report) => {
  if (report === "retailer") return "retailer";
  if (report === "product") return "product_id";
  return "province";
};

const baseRangeFilter = (range = {}) => {
  const and = [
    { unit_type: { _eq: "weight" } },
    { unit: { _eq: "kg" } },
  ];

  if (range.startDate) {
    and.push({
      record_at: {
        _gte: range.startDate,
      },
    });
  }

  if (range.endDate) {
    and.push({
      record_at: {
        _lte: range.endDate,
      },
    });
  }

  return and;
};

/** Snapshot slice: date bounds + weight/kg + single retailer (no time bucket, no overview cross-slice). */
const buildRetailerAnalyticsFilter = (range, retailerName) => {
  const r = coerceRangeForPriceHistoryFilter(range);
  const and = baseRangeFilter(r);
  if (retailerName) {
    and.push({ retailer: { _eq: retailerName } });
  }
  return { _and: and };
};

const withReportFilters = (report, and = [], filters = {}) => {
  if (report === "retailer" && filters?.retailers?.length) {
    and.push({
      retailer: {
        _in: filters.retailers,
      },
    });
  }

  if (report === "product" && filters?.products?.length) {
    and.push({
      product_id: {
        _in: filters.products,
      },
    });
  }

  if (report === "province" && filters?.provinces?.length) {
    and.push({
      _or: buildProvinceQueryClauses(filters.provinces),
    });
  }

  return and;
};

/** Overview stacked charts: AND retailer + product + province for every `report` query. */
const applyOverviewCrossSliceFilters = (andPredicateAccumulator, filters = {}) => {
  if (filters?.retailers?.length) {
    andPredicateAccumulator.push({
      retailer: {
        _in: filters.retailers,
      },
    });
  }

  if (filters?.products?.length) {
    andPredicateAccumulator.push({
      product_id: {
        _in: filters.products,
      },
    });
  }

  if (filters?.provinces?.length) {
    andPredicateAccumulator.push({
      _or: buildProvinceQueryClauses(filters.provinces),
    });
  }

  return andPredicateAccumulator;
};

const buildCommonFilter = (report, params = {}) => {
  const and = baseRangeFilter(params.range);
  if (params.trendFilterMode === "overviewCrossSlice") {
    applyOverviewCrossSliceFilters(and, params.filters);
  } else {
    withReportFilters(report, and, params.filters);
  }
  return {
    _and: and,
  };
};

const getPriceTrend = async (report, params = {}) => {
  const granularity = params?.granularity || "month";
  const groupBy = [...(bucketMap[granularity] || bucketMap.month)];
  const dimField = getDimField(report);

  groupBy.push(dimField);

  return req("price_history", {
    aggregate: {
      avg: ["unit_price"],
      count: "*",
    },
    groupBy,
    filter: buildCommonFilter(report, params),
    fields: ["record_at", "product_id.id", "product_id.name", "product_name"],
    limit: -1,
  });
};

const getPriceRows = async (report, params = {}) => {
  return req("price_history", {
    limit: -1,
    sort: params?.sort || ["-record_at"],
    filter: buildCommonFilter(report, params),
    fields: [
      "id",
      "record_at",
      "retailer",
      "province",
      "product_id.id",
      "product_id.name",
      "product_name",
      "unit_price",
      "price",
      "unit",
      "provider",
      "raw_product_name",
      "raw_unit",
      "raw_unit_price",
    ],
  });
};

const getRetailers = async () => {
  const rows = await req("knowledge_retailer", {
    fields: ["id", "name"],
    sort: ["name"],
    limit: -1,
  });
  return rows.filter((x) => x?.name).map((x) => x.name);
};

const getProducts = async () => {
  return req("product", {
    fields: ["id", "name", "status"],
    sort: ["name"],
    filter: {
      status: {
        _neq: "archived",
      },
    },
    limit: -1,
  });
};

const getProvinceOpts = async () => {
  const rows = await req("price_history", {
    fields: ["province"],
    groupBy: ["province"],
    aggregate: {
      count: "*",
    },
    limit: -1,
  });

  const list = rows
    .map((x) => x?.province)
    .filter(Boolean)
    .map((name) => toProv(name))
    .filter(Boolean);

  return getProvinceOptionList(list);
};

/**
 * Retailer Analytics (Overview block): per-product avg unit price and row count for one retailer.
 * Returns [] when `retailerName` is missing. Rows: `product_id` (id/name), `avg->unit_price`, `count`.
 * No calendar bucket in `groupBy` (snapshot bars, not time series).
 */
const getRetailerAnalyticsProductSnapshot = async ({ range, retailerName }) => {
  if (!retailerName) return [];
  return req("price_history", {
    aggregate: {
      avg: ["unit_price"],
      count: "*",
    },
    groupBy: ["product_id"],
    filter: buildRetailerAnalyticsFilter(range, retailerName),
    fields: ["product_id.id", "product_id.name", "product_name"],
    limit: -1,
  });
};

/**
 * Distinct product count per province for one retailer: Directus rows grouped by province + product_id;
 * client reduces to `{ province, distinctProductCount }`. Returns [] when `retailerName` is missing.
 */
const getRetailerAnalyticsDistinctProductCountByProvince = async ({ range, retailerName }) => {
  if (!retailerName) return [];
  const rows = await req("price_history", {
    aggregate: {
      count: "*",
    },
    groupBy: ["province", "product_id"],
    filter: buildRetailerAnalyticsFilter(range, retailerName),
    limit: -1,
  });

  const tallyByProvince = {};
  (rows || []).forEach((row) => {
    const province = row?.province;
    if (province == null || province === "") return;
    const productId = normalizeAggregateProductIdFromRow(row);
    if (!productId) return;
    if (!tallyByProvince[province]) tallyByProvince[province] = new Set();
    tallyByProvince[province].add(productId);
  });

  return Object.entries(tallyByProvince).map(([province, idSet]) => ({
    province,
    distinctProductCount: idSet.size,
  }));
};

export {
  getPriceTrend,
  getPriceRows,
  getRetailers,
  getProducts,
  getProvinceOpts,
  getRetailerAnalyticsProductSnapshot,
  getRetailerAnalyticsDistinctProductCountByProvince,
};
